import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import { Dispatch, FC, SetStateAction, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AttemptInformation } from '../../models/AttemptInformation';
import { PreviousAttempts } from '../../models/PreviousAttempts';
import { SkillResourceSteps } from '../../models/Timeline';
import { SupportSkills } from '../../models/SupportSkills';
import { Link } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  PreviousAndNextSSS,
  selectedStudentSkillStateAfterTakeAssessment,
  setStudentSkillAfterTakeAssessment,
} from '../../redux/reducers/studentSkillAfterTakeAssessmentSlice';
import { StudentSkillStates } from '../../models/StudentSkillState';
import { SkillSubStates } from '../../models/SkillState';
import { PencilRuler } from 'lucide-react';
import IceCubesResult from '../Dashboard/DashboardIcons/IceCubesResult';
import { getMotivationalMessage, resultTitleMap } from '../../businessLogicService/assessmentCompletedHelper';
import { requestFullscreen } from '../../utils/fullscreen';
import AssessmentCompletedStroke from './AssessmentCompletedStroke';
import { motion } from 'motion/react';
import WeeklyGoalEphemeralMessage, { WeeklyGoalMissedReason } from '../WeeklyGoal/WeeklyGoalEphemeralMessage';
import { hasPriorCredit, isWeeklyGoalEligible, PASSING_SCORE } from '../../businessLogicService/weeklyGoalEligibility';
import { useFlag } from '@unleash/proxy-client-react';
import { useBrainBreakNavigation } from '../../custom-hooks/useBrainBreakNavigation';
import { LTS_SHUTOFF_PP_435_WEEKLY_GOAL_FLAG } from '../../contexts/WeeklyGoalContext';
import { ChallengePhase } from '../AssessmentComponent/AssessmentComponent';
import { useCooldown } from '../../contexts/RetakeCountdownContext';
import RetakeCountdownBar from '../RetakeCountdown/RetakeCountdownBar';
import { usePreviewMode } from '../../contexts/PreviewModeContext';

export const LEARN_FROM_ERRORS_DEMO_TOOLTIP =
  'Disabled in the interactive demo, but students will be able to review their questions on their platform.';

interface Props {
  attemptInformation: AttemptInformation;
  setAttemptInformation: (info: AttemptInformation | null) => void;
  previousAttempts: PreviousAttempts[];
  incompleteEssentialSkills: SupportSkills[];
  workedExampleUrl: string | null | undefined;
  sssAfterTakeAssessment: PreviousAndNextSSS;
  setReviewDialogVisible: Dispatch<SetStateAction<boolean>>;
  onMoveForward?: () => void;
  moveForwardLabel?: string;
  setCurrentStep: Dispatch<SetStateAction<SkillResourceSteps | null>>;
  refetchLrnSession: () => void;
  skipNewActivityId: boolean;
  setSkipNewActivityId: Dispatch<SetStateAction<boolean>>;
  setAssessmentInProgress: Dispatch<SetStateAction<boolean>>;
  setPhase: Dispatch<SetStateAction<ChallengePhase>>;
  /** Substate as of page load, before this attempt was submitted. See SkillResource. */
  preAttemptSubstateId: string | null;
}

export const AssessmentCompleted: FC<Props> = ({
  incompleteEssentialSkills,
  attemptInformation,
  setAttemptInformation,
  previousAttempts,
  workedExampleUrl,
  sssAfterTakeAssessment,
  setReviewDialogVisible,
  onMoveForward,
  moveForwardLabel,
  setCurrentStep,
  refetchLrnSession,
  skipNewActivityId,
  setSkipNewActivityId,
  setAssessmentInProgress,
  setPhase,
  preAttemptSubstateId,
}) => {
  const { skillCode } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const previewMode = usePreviewMode();
  const sSAfterTakeAssessmentRedux = useAppSelector(selectedStudentSkillStateAfterTakeAssessment);
  const isWeeklyGoalEnabled = !useFlag(LTS_SHUTOFF_PP_435_WEEKLY_GOAL_FLAG);

  const cooldownCtx = useCooldown();
  const isBlocked = cooldownCtx?.isBlocked(skillCode || '');
  const remainingTime = cooldownCtx?.getRemainingTime(skillCode || '');

  // Shown in place of the celebration whenever this pass does not move the weekly goal, so it is
  // the literal negation of the rule the celebration uses rather than a parallel re-derivation.
  // `previousAttempts` already has the just-submitted attempt prepended unless the session was
  // resumed, which is what the last argument tells the rule to discount.
  const triggerWeeklyGoalMessage = !isWeeklyGoalEligible(previousAttempts, preAttemptSubstateId, !skipNewActivityId);
  const weeklyGoalMissedReason: WeeklyGoalMissedReason = hasPriorCredit(preAttemptSubstateId)
    ? 'alreadyInferred'
    : previousAttempts.length >= 3
    ? 'manyAttempts'
    : 'alreadyPassed';

  const { title, subtitle } = resultTitleMap(attemptInformation.totalScore, attemptInformation.scores.length);
  const { motivationalMessage } = getMotivationalMessage(attemptInformation.totalScore);

  const correctAnswers = attemptInformation.scores
    .filter(el => el.score === 2)
    .map(el => {
      return { score: el.score, maxScore: 2 };
    });

  const wrongAnswers = attemptInformation.scores
    .filter(el => el.score !== 2)
    .map(el => {
      return { score: el.score, maxScore: 2 };
    });

  // Determine assessment variant for brain break tracking
  // Score >= 8 out of 10 (4 out of 5 stars) is considered passed
  const assessmentVariant = attemptInformation.totalScore >= 8 ? 'passed' : 'failed';
  const assessmentPlacement = `assessment-completed_${assessmentVariant}` as const;

  const handleMoveForward = useBrainBreakNavigation(onMoveForward ?? (() => navigate('/')), assessmentPlacement);
  const handleEssentialSkills = useBrainBreakNavigation(() => {
    const skillCode = incompleteEssentialSkills[0]?.skill?.code;
    if (skillCode) {
      navigate(`/skill/${skillCode}`);
    }
  }, assessmentPlacement);

  const handleWorkedExample = useBrainBreakNavigation(
    () => setCurrentStep(SkillResourceSteps.WORKED_EXAMPLE),
    assessmentPlacement,
  );

  const handlePracticeProblem = useBrainBreakNavigation(
    () => setCurrentStep(SkillResourceSteps.PRACTICE_PROBLEMS),
    assessmentPlacement,
  );

  const handleSkillChallenge = useBrainBreakNavigation(() => {
    setSkipNewActivityId(false);
    setAssessmentInProgress(true);
    setCurrentStep(SkillResourceSteps.SKILL_CHALLENGE);
    refetchLrnSession();
    setPhase('PRE');
  }, assessmentPlacement);

  const buttons = {
    workedExample: (outlinedAndRounded: boolean, label?: string) => (
      <Button
        style={{ display: !workedExampleUrl ? 'none' : 'inline' }}
        className="tw-font-atkinson"
        label={label ? label : 'Worked example video'}
        onClick={handleWorkedExample}
        outlined={outlinedAndRounded}
        rounded={outlinedAndRounded}
      />
    ),
    practiceProblem: (outlinedAndRounded: boolean) => (
      <Button
        className="tw-font-atkinson"
        label="Get more practice"
        onClick={handlePracticeProblem}
        outlined={outlinedAndRounded}
        rounded={outlinedAndRounded}
      />
    ),
    essentialSkill: (
      <Link
        style={{ display: 'inline-flex' }}
        className={`tw-font-atkinson tw-bg-blue-500 tw-text-white tw-items-center tw-border-1 tw-rounded-full tw-font-semibold tw-p-[9px] hover:tw-bg-[#17275d] hover:tw-text-white`}
        data-testid="Predecessor-btn"
        onClick={handleEssentialSkills}
        to={`${incompleteEssentialSkills.length ? `/skill/${incompleteEssentialSkills[0].skill?.code}}` : '#'} `}
        id="predecessorLink"
      >
        Try an essential skill
      </Link>
    ),
    skillChallenge: (label: string, withoutBorder: boolean) => (
      <Button
        className="tw-font-atkinson"
        label={label}
        onClick={e => {
          if (!previewMode) {
            requestFullscreen();
          }
          handleSkillChallenge(e);
        }}
        outlined
        rounded
        style={{ border: withoutBorder ? 'none' : '' }}
      />
    ),
    moveForward: (
      <Button
        data-testid="Home-btn"
        label={moveForwardLabel ?? 'Move Forward'}
        className="tw-font-atkinson"
        onClick={handleMoveForward}
        id="returnHome"
      />
    ),
  };

  const buttonsWithCountdown = (score: number) => (
    <div className="tw-bg-indigo-100 tw-flex tw-flex-row tw-items-center tw-justify-center tw-p-[20px] tw-rounded-[1000px] tw-w-full">
      <div className="tw-w-3/6">
        {remainingTime && remainingTime > 0 ? (
          <RetakeCountdownBar skillCode={skillCode as string} width="100%" background="white" />
        ) : (
          buttons.skillChallenge('Retry skill challenge', false)
        )}
      </div>
      <div className="tw-w-3/6 tw-gap-x-2 tw-flex tw-items-center tw-justify-center">
        <span className="tw-font-atkinson tw-text-sm tw-font-bold">Don’t want to wait?</span>
        {score === 8 ? buttons.moveForward : buttons.practiceProblem(false)}
      </div>
    </div>
  );

  const renderButtonsNew = () => {
    if (
      attemptInformation.totalScore === 0 ||
      attemptInformation.totalScore === 2 ||
      attemptInformation.totalScore === 4
    ) {
      if (previousAttempts.length === 1) {
        return [buttons.workedExample(true), buttons.practiceProblem(false)];
      }
      if (previousAttempts.length === 2) {
        return [buttons.practiceProblem(true), buttons.workedExample(false)];
      }
      if (previousAttempts.length >= 3) {
        return [
          buttons.practiceProblem(true),
          incompleteEssentialSkills.length
            ? buttons.essentialSkill
            : buttons.workedExample(false, 'Back to foundational knowledge'),
        ];
      }
    }
    if (attemptInformation.totalScore === 6) {
      if (previousAttempts.length === 1 || previousAttempts.length === 2) {
        return isBlocked
          ? buttonsWithCountdown(attemptInformation.totalScore)
          : [buttons.skillChallenge('Retry skill challenge', false), buttons.practiceProblem(false)];
      }
      if (previousAttempts.length >= 3) {
        return isBlocked
          ? buttonsWithCountdown(attemptInformation.totalScore)
          : [buttons.skillChallenge('Retry skill challenge', false), buttons.workedExample(false)];
      }
    }
    if (attemptInformation.totalScore === 8) {
      return isBlocked
        ? buttonsWithCountdown(attemptInformation.totalScore)
        : [buttons.skillChallenge('Try for a perfect score', true), buttons.moveForward];
    }
    if (attemptInformation.totalScore === 10) {
      return [buttons.moveForward];
    }
  };

  useEffect(() => {
    if (!skipNewActivityId) {
      // Determine the appropriate skill substate based on the score
      const determineSubstateFromScore = (score: number): { id: string; name?: string } => {
        if (score >= PASSING_SCORE) {
          return {
            id: SkillSubStates.PASSED_VIA_ASSESSMENT,
            name: 'Passed via Assessment',
          };
        } else {
          return {
            id: SkillSubStates.NOT_PASSED_VIA_ASSESSMENT,
            name: 'Not Passed via Assessment',
          };
        }
      };

      const updatedSkillSubstate = determineSubstateFromScore(attemptInformation.totalScore);

      const updatedSSAfterTakeAssessment = {
        ...sssAfterTakeAssessment.previousState,
        skillAssessment: {
          id: 'SkillAssessmentNode:0',
          score: attemptInformation.totalScore,
          isComplete: true,
        },
        skillSubstate: updatedSkillSubstate,
      };

      if (
        sSAfterTakeAssessmentRedux?.nextState?.skillAssessment?.score !==
          updatedSSAfterTakeAssessment.skillAssessment.score ||
        updatedSSAfterTakeAssessment.skill?.id !== sssAfterTakeAssessment.nextState?.skill.id
      ) {
        dispatch(
          setStudentSkillAfterTakeAssessment({
            previousState: sssAfterTakeAssessment.previousState,
            nextState: updatedSSAfterTakeAssessment as StudentSkillStates,
          }),
        );
      }
    }
  }, [skipNewActivityId]);

  useEffect(() => {
    return () => {
      setAttemptInformation(null);
    };
  }, []);

  return (
    <>
      <div className="tw-flex tw-flex-row tw-ml-auto tw-p-6 tw-items-center">
        <PencilRuler />
        <span className="sm:tw-text-2xl tw-text-lg tw-font-sora tw-font-bold tw-ml-4">Skill Challenge</span>
      </div>
      <div className="tw-flex tw-flex-col tw-items-center tw-py-[24px] tw-px-[12px] tw-gap-y-6">
        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{
            duration: 0.5,
            delay: 1.2,
            ease: [0.34, 1.56, 0.64, 1],
          }}
          className="tw-flex tw-flex-col tw-gap-y-2 tw-items-center"
        >
          <h3 className={`tw-font-atkinson tw-font-normal sm:tw-text-4xl tw-text-xl`}>{title}</h3>
          <p className="tw-font-atkinson tw-font-normal tw-text-lg">{subtitle}</p>
          <Button
            className="nc_learn-from-errors-disabled"
            data-testid="learn-from-errors-btn"
            disabled={previewMode}
            label={`${
              attemptInformation.totalScore === 8
                ? 'Show me the error'
                : attemptInformation.totalScore === 10
                ? 'Review results'
                : 'Learn from errors'
            }`}
            outlined
            rounded
            style={{ border: 'none', zIndex: '2' }}
            onClick={() => setReviewDialogVisible(true)}
          />
          {previewMode && (
            <Tooltip
              target=".nc_learn-from-errors-disabled"
              showOnDisabled
              position="top"
              className="nc_demo-disabled-tooltip tw-w-72"
            >
              {LEARN_FROM_ERRORS_DEMO_TOOLTIP}
            </Tooltip>
          )}
        </motion.div>

        <IceCubesResult
          correctAnswers={correctAnswers}
          wrongAnswers={wrongAnswers}
          isPerfectScore={wrongAnswers.length === 0}
          passedScore={attemptInformation.totalScore >= 8}
        />

        <motion.span
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{
            duration: 0.5,
            delay: 1.2,
            ease: [0.34, 1.56, 0.64, 1],
          }}
          className="nc_assessment-completed-span tw-p-4 sm:tw-text-lg tw-text-md tw-font-atkinson tw-gap-[5px] sm:tw-w-[500px] tw-w-[395px]"
        >
          {motivationalMessage}
        </motion.span>
        <div
          className={`tw-flex tw-flex-row tw-gap-2 ${
            isBlocked && (attemptInformation.totalScore === 6 || attemptInformation.totalScore === 8)
              ? 'tw-w-[80%]'
              : ''
          }`}
        >
          {renderButtonsNew()}
        </div>
        <motion.span
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{
            duration: 0.5,
            delay: 1.2,
            ease: [0.34, 1.56, 0.64, 1],
          }}
        >
          <AssessmentCompletedStroke
            isBlocked={isBlocked && (attemptInformation.totalScore === 6 || attemptInformation.totalScore === 8)}
          />
        </motion.span>
      </div>
      {isWeeklyGoalEnabled && triggerWeeklyGoalMessage && attemptInformation.totalScore >= PASSING_SCORE && (
        <WeeklyGoalEphemeralMessage reason={weeklyGoalMissedReason} />
      )}
    </>
  );
};
